// Keyboard / mouse state, sampled once per frame by the app.

export class Input {
  private readonly down = new Set<string>();
  private readonly pressedFrame = new Set<string>();
  mouseDX = 0;
  mouseDY = 0;
  wheel = 0;
  dragging = false;
  pointerLocked = false;
  sensitivity = 1;
  private readonly canvas: HTMLElement;

  constructor(canvas: HTMLElement) {
    this.canvas = canvas;
    window.addEventListener('keydown', (e) => {
      if (isTyping(e)) return;
      if (!this.down.has(e.code)) this.pressedFrame.add(e.code);
      this.down.add(e.code);
      if (['Tab', 'Space', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.code)) e.preventDefault();
    });
    window.addEventListener('keyup', (e) => this.down.delete(e.code));
    window.addEventListener('blur', () => this.down.clear());
    canvas.addEventListener('mousedown', (e) => {
      if (e.button === 0 || e.button === 2) this.dragging = true;
    });
    window.addEventListener('mouseup', () => (this.dragging = false));
    window.addEventListener('mousemove', (e) => {
      if (this.dragging || this.pointerLocked) {
        this.mouseDX += e.movementX * this.sensitivity;
        this.mouseDY += e.movementY * this.sensitivity;
      }
    });
    canvas.addEventListener('wheel', (e) => {
      this.wheel += Math.sign(e.deltaY) * Math.min(3, Math.abs(e.deltaY) / 60);
      e.preventDefault();
    }, { passive: false });
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());
    document.addEventListener('pointerlockchange', () => {
      this.pointerLocked = document.pointerLockElement === this.canvas;
    });
  }

  isDown(code: string): boolean {
    return this.down.has(code);
  }

  /** True only on the frame the key went down. */
  pressed(code: string): boolean {
    return this.pressedFrame.has(code);
  }

  axis(neg: string[], pos: string[]): number {
    let v = 0;
    if (neg.some((c) => this.down.has(c))) v -= 1;
    if (pos.some((c) => this.down.has(c))) v += 1;
    return v;
  }

  requestLock(): void {
    if (!this.pointerLocked) this.canvas.requestPointerLock?.();
  }

  releaseLock(): void {
    if (this.pointerLocked) document.exitPointerLock?.();
  }

  /** Call at the end of each frame. */
  endFrame(): void {
    this.pressedFrame.clear();
    this.mouseDX = 0;
    this.mouseDY = 0;
    this.wheel = 0;
  }
}

function isTyping(e: KeyboardEvent): boolean {
  const t = e.target as HTMLElement | null;
  return !!t && (t.tagName === 'INPUT' || t.tagName === 'SELECT' || t.tagName === 'TEXTAREA');
}
